import { ErrorMessage, Input } from '@iwoca/orion';
import classNames from 'classnames';
import {
    FieldErrors,
    UseFormRegister,
    UseFormSetValue,
    UseFormWatch
} from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import styles from './AddressQuestion.module.css';
import { AutoAddress } from '../../../../forms/autoAddress';
import { buildAddressLabel } from '../../../../forms/autoAddress/AutoAddressHelper';
import { ManualAddressRHF } from '../../../../forms/manualAddress/ManualAddressRHF';
import { supportsAutoAddress } from '../../../../helpers/address';
import {
    TDeserialisedAddresses,
    TPersonFormValues
} from '../PersonModal.types';

type AddressQuestionProps = {
    addressIndex: number;
    register: UseFormRegister<TPersonFormValues>;
    setValue: UseFormSetValue<TPersonFormValues>;
    watch: UseFormWatch<TPersonFormValues>;
    onManualAddressToggle: (addressIndex: number) => void;
    fetchedAddresses: TDeserialisedAddresses;
    fetchAddresses: (postcode: string) => void;
    showManualAddresses: Record<number, boolean>;
    hasCreditDecision: boolean;
    isEditingPerson: boolean;
    isGuarantor: boolean;
    isApplicant: boolean;
    errors: FieldErrors<TPersonFormValues>;
};

export const AddressQuestion = ({
    addressIndex,
    register,
    setValue,
    watch,
    onManualAddressToggle,
    fetchedAddresses,
    fetchAddresses,
    showManualAddresses,
    hasCreditDecision,
    isEditingPerson,
    isGuarantor,
    isApplicant,
    errors
}: AddressQuestionProps) => {
    const showManualAddress =
        showManualAddresses?.[addressIndex] || !supportsAutoAddress();
    const disableEditingAdditionalGuarantor =
        hasCreditDecision && isEditingPerson && isGuarantor && !isApplicant;

    if (disableEditingAdditionalGuarantor) {
        return (
            <div
                className={classNames(
                    styles.disabled_address_field,
                    'fs-exclude'
                )}
            >
                {/* eslint-disable-next-line iwoca/input-a11y */}
                <Input
                    value={buildAddressLabel(fetchedAddresses[addressIndex])}
                    disabled={true}
                    data-testid="non-editable-address-input"
                />
            </div>
        );
    }

    if (showManualAddress) {
        const addressErrors = (errors?.addresses as any)?.[addressIndex];
        return (
            <ManualAddressRHF
                addressIndex={addressIndex}
                register={register}
                onManualAddressToggle={onManualAddressToggle}
                errors={addressErrors}
            />
        );
    }

    const input = {
        value: watch(`addresses.${addressIndex}` as `addresses.${number}`),
        onChange: (address: unknown) => {
            setValue(
                `addresses.${addressIndex}` as `addresses.${number}`,
                address as TPersonFormValues['addresses'][number],
                { shouldDirty: true }
            );
        }
    };

    const addressError = (errors?.addresses as any)?.[addressIndex];
    const hasAddressError =
        addressError && Object.keys(addressError).length > 0;

    const errorElement = hasAddressError ? (
        <ErrorMessage className="mt-s">
            <FormattedMessage
                id="_.auto_address.error.enter_address"
                defaultMessage="Please enter at least one address"
            />
        </ErrorMessage>
    ) : null;

    return (
        <AutoAddress
            input={input}
            addressIndex={addressIndex}
            onManualAddressToggle={onManualAddressToggle}
            fetchedAddresses={fetchedAddresses}
            onChange={fetchAddresses}
            errorElement={errorElement}
        />
    );
};
